(() => {
  if (window.NPSStickyProduct) {
    window.NPSStickyProduct.connect();
    return;
  }
  const selector = '.nps-sticky-cart-docked';
  const footerSelector = '#xo-root > .shopify-section-group-footer-group.xo-section-footer';
  let bar = null;
  let product = null;
  let buttons = [];
  let footers = [];
  let originalAriaHidden = null;
  let originalInert = false;
  let frame = 0;
  let listening = false;
  let resizeObserver;
  let intersectionObserver;
  let footerIntersectionObserver;
  let productObserver;
  let lifecycleObserver;

  const reserveSpace = () => {
    const hidesAtFooter = bar?.hasAttribute('data-nps-sticky-footer-visibility') && document.querySelector(footerSelector);
    const height = bar?.isConnected && !hidesAtFooter ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--nps-sticky-cart-height', `${height}px`);
  };
  const restoreAccessibility = () => {
    if (!bar) return;
    bar.toggleAttribute('inert', originalInert);
    if (originalAriaHidden === null) bar.removeAttribute('aria-hidden');
    else bar.setAttribute('aria-hidden', originalAriaHidden);
  };
  const setVisible = (visible, renderedButtons) => {
    if (!visible && bar.contains(document.activeElement)) {
      const mainButton = renderedButtons.find(({ inView }) => inView)?.button;
      if (mainButton && !mainButton.disabled) mainButton.focus({ preventScroll: true });
      if (bar.contains(document.activeElement)) document.activeElement.blur();
    }
    bar.toggleAttribute('data-nps-sticky-visible', visible);
    if (visible) restoreAccessibility();
    else {
      bar.setAttribute('inert', '');
      bar.setAttribute('aria-hidden', 'true');
    }
  };
  const syncButtons = () => {
    const current = product ? [...product.querySelectorAll('.xo-buy-buttons__add-to-cart-detail button')].filter((button) =>
      button.closest('xo-product') === product &&
      !button.closest('xo-sticky, xo-modal, xo-popover') &&
      !button.classList.contains('xo-visually-hidden')
    ) : [];
    if (current.length === buttons.length && current.every((button, index) => button === buttons[index])) return;
    intersectionObserver?.disconnect();
    buttons.forEach((button) => resizeObserver.unobserve(button));
    buttons = current;
    buttons.forEach((button) => {
      intersectionObserver?.observe(button);
      resizeObserver.observe(button);
    });
  };
  const syncFooters = () => {
    const current = bar.hasAttribute('data-nps-sticky-footer-visibility') ? [...document.querySelectorAll(footerSelector)] : [];
    if (current.length === footers.length && current.every((footer, index) => footer === footers[index])) return;
    footerIntersectionObserver?.disconnect();
    footers.forEach((footer) => resizeObserver.unobserve(footer));
    footers = current;
    footers.forEach((footer) => {
      footerIntersectionObserver?.observe(footer);
      resizeObserver.observe(footer);
    });
  };
  const update = () => {
    frame = 0;
    if (!bar?.isConnected) {
      connect();
      return;
    }
    reserveSpace();
    syncFooters();
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop ?? 0;
    const left = viewport?.offsetLeft ?? 0;
    const bottom = top + (viewport?.height ?? window.innerHeight);
    const right = left + (viewport?.width ?? window.innerWidth);
    const footerInView = footers.some((footer) => {
      const bounds = footer.getBoundingClientRect();
      const style = getComputedStyle(footer);
      return bounds.width > 0 && bounds.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && bounds.bottom > top && bounds.top < bottom && bounds.right > left && bounds.left < right;
    });
    bar.toggleAttribute('data-nps-sticky-footer-hidden', footerInView);
    if (!bar.hasAttribute('data-nps-sticky-visibility')) {
      bar.removeAttribute('data-nps-sticky-visible');
      if (footerInView) setVisible(false, []);
      else restoreAccessibility();
      return;
    }
    syncButtons();
    const renderedButtons = buttons.flatMap((button) => {
      const bounds = button.getBoundingClientRect();
      const style = getComputedStyle(button);
      if (bounds.width <= 0 || bounds.height <= 0 || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return [];
      return [{ button, bounds, inView: bounds.bottom > top && bounds.top < bottom && bounds.right > left && bounds.left < right }];
    });
    const passedMainCart = renderedButtons.length > 0 && renderedButtons.every(({ bounds }) => bounds.bottom <= top);
    setVisible(passedMainCart && !footerInView, renderedButtons);
  };
  const scheduleUpdate = () => {
    if (bar && !frame) frame = requestAnimationFrame(update);
  };
  const setListeners = (enabled) => {
    if (listening === enabled) return;
    listening = enabled;
    const method = enabled ? 'addEventListener' : 'removeEventListener';
    window[method]('resize', scheduleUpdate, { passive: true });
    window[method]('scroll', scheduleUpdate, { passive: true });
    window.visualViewport?.[method]('resize', scheduleUpdate, { passive: true });
    window.visualViewport?.[method]('scroll', scheduleUpdate, { passive: true });
  };
  const disconnect = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    restoreAccessibility();
    resizeObserver?.disconnect();
    intersectionObserver?.disconnect();
    footerIntersectionObserver?.disconnect();
    productObserver?.disconnect();
    setListeners(false);
    buttons = [];
    footers = [];
    bar = null;
    product = null;
    reserveSpace();
  };
  const connect = () => {
    const current = document.querySelector(selector);
    if (bar === current) {
      scheduleUpdate();
      return;
    }
    disconnect();
    if (!current) return;
    if (!resizeObserver) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(scheduleUpdate, { threshold: 0 }) : null;
      footerIntersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(scheduleUpdate, { threshold: 0 }) : null;
      productObserver = new MutationObserver(scheduleUpdate);
    }
    bar = current;
    product = bar.closest('xo-product[xo-product-information]');
    originalAriaHidden = bar.getAttribute('aria-hidden');
    originalInert = bar.hasAttribute('inert');
    resizeObserver.observe(bar);
    if (product) {
      resizeObserver.observe(product);
      productObserver.observe(product, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'xo-disabled', 'xo-unavailable'] });
    }
    setListeners(true);
    update();
  };
  const hasElement = (node, value) => node.nodeType === 1 && (node.matches(value) || node.querySelector(value));
  const ready = () => {
    if (!lifecycleObserver && document.body) {
      lifecycleObserver = new MutationObserver((records) => {
        if (bar && !bar.isConnected) {
          connect();
          return;
        }
        if (records.some((record) => [...record.addedNodes].some((node) => hasElement(node, selector)))) {
          connect();
          return;
        }
        if (bar && records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => hasElement(node, '.shopify-section-group-footer-group.xo-section-footer')))) scheduleUpdate();
      });
      lifecycleObserver.observe(document.body, { childList: true, subtree: true });
    }
    connect();
  };
  window.NPSStickyProduct = { connect: ready };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  document.addEventListener('shopify:section:load', ready);
  document.addEventListener('shopify:section:unload', (event) => {
    if (bar && event.target.contains(bar)) disconnect();
    else scheduleUpdate();
  });
  window.addEventListener('pageshow', ready);
})();
